import React, { useState } from 'react';
import { Certificate, ShieldCheck } from '@phosphor-icons/react';
import EducationSheet from './EducationSheet';
import EducationCallout from './EducationCallout';
import InvestmentStructureContent from './InvestmentStructureContent';
import OwnershipExplainerContent from './OwnershipExplainerContent';
import PropertyParticipationCard from './PropertyParticipationCard';
import {
  useInstrumentEconomics, money, compactMoney, pct, monthsLabel, INVESTOR_BLUE,
} from './instrumentEconomics';

const Overline = ({ children, className = '' }) => (
  <p className={`text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-foreground-muted ${className}`}>
    {children}
  </p>
);

const Metric = ({ value, label, color, testId, size = 'md', hint }) => (
  <div>
    <p
      className={`${size === 'lg'
        ? 'text-[30px] md:text-[38px]'
        : 'text-[20px] md:text-[24px]'} leading-none font-semibold tracking-tight tabular-nums`}
      style={color ? { color } : undefined}
      data-testid={testId}
    >
      {value}
    </p>
    <p className={`text-foreground-muted ${size === 'lg' ? 'text-xs mt-1.5' : 'text-[11px] md:text-xs mt-1.5'}`}>{label}</p>
    {hint && (
      <p className="text-[11px] text-foreground-muted/80 mt-1 tabular-nums" data-testid={testId ? `${testId}-breakdown` : undefined}>
        {hint}
      </p>
    )}
  </div>
);

/** Where the projected return is expected to come from, when the instrument records the split. */
const returnBreakdown = (data) => {
  const parts = [];
  if (data.yieldPct !== null && data.yieldPct > 0) parts.push(`Rental yield ${pct(data.yieldPct)}/yr`);
  if (data.appreciationReturnPct !== null && data.appreciationReturnPct > 0) {
    parts.push(`Appreciation ${pct(data.appreciationReturnPct)} over the hold`);
  }
  return parts.length ? parts.join(' · ') : null;
};

/**
 * One responsive Flippex investment experience: identical data, terminology,
 * colors and educational content at every breakpoint - only the composition adapts.
 */
const InstrumentSummary = ({ instrument, currency = 'EUR' }) => {
  const [structureOpen, setStructureOpen] = useState(false);
  const [ownershipOpen, setOwnershipOpen] = useState(false);
  const data = useInstrumentEconomics(instrument);

  if (!data) return null;

  const surface = 'bg-white rounded-2xl border border-black/[0.05] px-5 py-5 md:px-7 md:py-6';

  return (
    <div className="w-full max-w-[1160px] space-y-4 md:space-y-5" data-testid="instrument-summary">
      {/* ============ 01 INVESTMENT INSTRUMENT ============ */}
      <section className={surface}>
        <div className="flex items-center gap-2">
          <Certificate size={17} className="text-primary" weight="duotone" />
          <h3 className="text-sm md:text-base font-heading">Investment Instrument</h3>
          {instrument.code && (
            <span
              className="ml-auto text-[10px] md:text-[11px] font-mono tracking-wide text-foreground-muted bg-slate-100 rounded-full px-2.5 py-1"
              data-testid="instrument-code"
            >
              {instrument.code}
            </span>
          )}
        </div>

        {/* Key economics */}
        <div className="mt-5 md:mt-6 grid gap-5 md:grid-cols-3 md:gap-8 md:items-end">
          <Metric
            value={money(data.sharePrice, currency)}
            label="per share"
            testId="share-price"
            size="lg"
          />
          <div className="flex flex-wrap gap-x-8 gap-y-4 md:contents">
            <Metric
              value={pct(data.primaryReturn)}
              label={data.annualReturn !== null ? 'Projected total return' : 'Projected IRR (annualized)'}
              hint={returnBreakdown(data)}
              color="rgb(5 150 105)"
              testId="projected-return"
            />
            <Metric
              value={data.targetHold ? monthsLabel(data.targetHold) : '—'}
              label="Target hold"
              testId="target-hold"
            />
          </div>
        </div>

        {data.annualReturn !== null && (
          <p className="mt-3.5 text-xs md:text-sm text-foreground-muted">
            Annualized IRR <span className="text-emerald-700/90 font-medium tabular-nums">{pct(data.annualReturn)}</span>
          </p>
        )}

        {/* Availability */}
        <div className="mt-6 md:mt-7 pt-5 md:pt-6 border-t border-black/[0.06]">
          <Overline>Investment availability</Overline>

          <div className="mt-3 flex flex-col gap-1.5 md:flex-row md:items-baseline md:justify-between">
            <p
              className="text-[20px] md:text-[24px] leading-none font-semibold tracking-tight tabular-nums"
              style={{ color: INVESTOR_BLUE }}
              data-testid="funded-percent"
            >
              {Math.round(data.fundedPercentage)}% funded
            </p>
            <p className="text-xs md:text-sm text-foreground-muted tabular-nums" data-testid="shares-available">
              {data.availableShares.toLocaleString()} of {data.totalShares.toLocaleString()} shares available
            </p>
          </div>

          <div className="mt-3 h-1.5 md:h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.min(Math.max(data.fundedPercentage, 0), 100)}%`,
                backgroundColor: INVESTOR_BLUE,
              }}
              data-testid="funding-bar"
            />
          </div>

          <div className="mt-2.5 flex items-baseline justify-between gap-4">
            <p className="text-xs md:text-sm text-foreground-muted tabular-nums" data-testid="remaining-capital">
              {compactMoney(data.remainingCapital, currency)} remaining
            </p>
            <p className="text-xs md:text-sm text-foreground-muted tabular-nums" data-testid="total-raise">
              <span className="text-[10px] md:text-[11px] uppercase tracking-[0.16em]">Total raise</span>{' '}
              <span className="font-medium text-slate-900">{money(data.totalRaise, currency)}</span>
            </p>
          </div>
        </div>
      </section>

      {/* ============ HOW YOUR INVESTMENT WORKS ============ */}
      <EducationCallout
        icon={ShieldCheck}
        title="How your investment works"
        body="Your investment is represented by a tokenized security linked to the financial performance of the underlying property. Smart contracts record and administer the instrument on the blockchain, while a security interest over the underlying real estate provides an additional layer of investor protection. Project financial performance is independently audited."
        ctaLabel="How your investment works"
        onOpen={() => setStructureOpen(true)}
        testId="investment-structure-callout"
      />

      {/* ============ 02 PROPERTY VALUE & PARTICIPATION ============ */}
      {data.hasOwnership && (
        <PropertyParticipationCard
          className={surface}
          currentValue={data.currentNav}
          baselineValue={data.startingNav}
          change={data.appreciation}
          changePct={data.appreciationPct}
          valuationDate={data.valuationDate}
          investorPercent={data.investorPercent}
          flippexPercent={data.flippexPercent}
          investorValue={data.investorValue}
          flippexValue={data.flippexValue}
          investedCapital={data.totalRaise}
          currency={currency}
          onExplain={() => setOwnershipOpen(true)}
        />
      )}

      <EducationSheet
        open={structureOpen}
        onOpenChange={setStructureOpen}
        title="How your investment works"
        description="What you are buying and how the instrument is structured."
        testId="investment-structure-sheet"
      >
        <InvestmentStructureContent investorPercent={data.investorPercent} />
      </EducationSheet>

      <EducationSheet
        open={ownershipOpen}
        onOpenChange={setOwnershipOpen}
        title="How participation works"
        description="Participation is fixed at launch and is not diluted by valuation changes."
        testId="ownership-explainer-sheet"
      >
        <OwnershipExplainerContent
          currentNav={data.currentNav}
          totalRaise={data.totalRaise}
          totalShares={data.totalShares}
          sharePrice={data.sharePrice}
          investorPercent={data.investorPercent}
          flippexPercent={data.flippexPercent}
          flippexValue={data.flippexValue}
          currency={currency}
        />
      </EducationSheet>
    </div>
  );
};

export default InstrumentSummary;
